import { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { LocalStorageAcceptableKeys } from '@/common/types';
import SortingIcon from '../SortingIcon/SortingIcon';
import {
  getLastLeftPinnedColumnStyles,
  getPinnedColumnStyles,
} from './TanTable';
import { ReactNode, useState } from 'react';
import ColumnResizeHandler from './ColumnResizeHandler';
import TableHeaderInfoTooltip from './TableHeaderInfoTooltip';

type TanTableHeadProps = {
  table: Table<any>;
  localStorageKey?: LocalStorageAcceptableKeys;
  isScrolledRight: boolean;
  enableColumnResizing?: boolean;
  stickyTopRows?: ReactNode[];
};

export default function TanTableHead({
  table,
  isScrolledRight,
  enableColumnResizing,
  stickyTopRows = [],
}: TanTableHeadProps) {
  const [isResizingViaHandle, setIsResizingViaHandle] = useState(false);

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <>
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const sortDirection = header.column.getIsSorted();
              const isSorted = sortDirection !== false;
              const canResize =
                !!enableColumnResizing && header.column.getCanResize();

              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{
                    width:
                      header.column.getSize() > 0
                        ? `${header.column.getSize()}px`
                        : 'auto',
                    ...getPinnedColumnStyles(header.column),
                    ...(isScrolledRight
                      ? getLastLeftPinnedColumnStyles(header.column)
                      : {}),
                    padding: canSort ? '0' : '0 8px',
                    cursor: canSort ? 'pointer' : 'default',
                  }}
                  onClickCapture={
                    canSort
                      ? (event) => {
                          if (isResizingViaHandle) {
                            event.stopPropagation();
                            setIsResizingViaHandle(false);
                          }
                        }
                      : undefined
                  }
                  onClick={
                    canSort
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {header.isPlaceholder ? null : (
                    <>
                      <div className="tan-table__header-content">
                        {canSort && (
                          <SortingIcon
                            isSortedByColumn={isSorted}
                            direction={
                              sortDirection === 'desc' ? 'desc' : 'asc'
                            }
                          />
                        )}
                        <span
                          className={`tan-table__header-label${
                            canResize ? ' line-clamp' : ''
                          }`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        {header.column.columnDef.meta?.headerInfoTooltip && (
                          <TableHeaderInfoTooltip
                            title={
                              header.column.columnDef.meta.headerInfoTooltip
                            }
                          />
                        )}
                      </div>
                      {canResize && (
                        <ColumnResizeHandler
                          header={header}
                          onResizeStart={() => setIsResizingViaHandle(true)}
                        />
                      )}
                    </>
                  )}
                </th>
              );
            })}
          </tr>
          {stickyTopRows.map((r) => r)}
        </>
      ))}
    </thead>
  );
}
